import {
  Alert,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useAIAction } from '../../hooks/useAIAction';
import { useState, useEffect } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  paragraphId?: string;
  bookId?: string;
  onNavigateToParagraph?: (paragraphId: string) => void;
}

interface FindSimilarResult {
  disabled?: boolean;
  results?: Array<{
    entity_type: string;
    entity_id: string;
    score: number;
    preview?: string | null;
  }>;
}

export function RelevantContextDrawer({
  open,
  onClose,
  paragraphId,
  bookId,
  onNavigateToParagraph,
}: Props) {
  const findSimilar = useAIAction('find-similar');
  const [result, setResult] = useState<FindSimilarResult | null>(null);

  useEffect(() => {
    if (!open || !paragraphId) {
      return;
    }
    let cancelled = false;
    (async () => {
      const outcome = await findSimilar.mutateAsync({
        entity_type: 'paragraph',
        entity_id: paragraphId,
        scope: bookId ? `book:${bookId}` : undefined,
        k: 10,
      });
      if (!cancelled) {
        setResult(
          (outcome.result as FindSimilarResult | undefined) ?? { results: [] },
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, paragraphId, bookId]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 420, p: 2 } }}
    >
      <Stack spacing={2}>
        <Typography variant="h6">Relevant context</Typography>
        {findSimilar.isPending && <CircularProgress size={20} />}
        {result?.disabled && (
          <Alert severity="info">
            Embeddings are disabled in this environment (local dev default).
            Enable
            <code> Features:Embeddings:Enabled=true</code> with real Azure
            OpenAI credentials.
          </Alert>
        )}
        {result &&
          result.results &&
          result.results.length === 0 &&
          !result.disabled && (
            <Typography variant="body2" color="text.secondary">
              No similar items yet. Embeddings populate in the background as you
              write.
            </Typography>
          )}
        <List>
          {result?.results?.map((hit) => (
            <ListItemButton
              key={`${hit.entity_type}:${hit.entity_id}`}
              onClick={() => {
                if (hit.entity_type === 'paragraph' && onNavigateToParagraph) {
                  onNavigateToParagraph(hit.entity_id);
                  onClose();
                }
              }}
            >
              <ListItemText
                primary={`${hit.entity_type} · ${Math.round(hit.score * 100)}% similar`}
                secondary={hit.preview ?? hit.entity_id}
              />
            </ListItemButton>
          ))}
        </List>
      </Stack>
    </Drawer>
  );
}
